import generateUUID from './generateUUID'
import FormValidation from './FormValidation'
import errorMessage from './errorMessage'

// Self-contained styles for a widget dialog relocated to the real document.body
// (outside the shadow root that holds the KB stylesheet). Mirrors the `.modal`,
// `.modal__content`, `.close` and `.modal--widget` rules from sass/components/_modal.scss.
const WIDGET_MODAL_CSS = `
.modal.modal--widget {
  position: fixed;
  inset: 0;
  width: auto;
  height: auto;
  padding: 0;
  margin: 0;
  overflow-x: hidden;
  overflow-y: auto;
  color: inherit;
  background-color: transparent;
  border: 0;
}
.modal.modal--widget::backdrop {
  background-color: rgba(0, 0, 0, 0.5);
}
.modal.modal--widget .modal__content {
  position: relative;
  display: flex;
  flex-direction: column;
  width: auto;
  max-width: 46em;
  max-height: 100vh;
  max-height: 100dvh;
  padding: 1em 0.75em;
  margin: 1em;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background-color: #fff;
  border-radius: 4px;
}
@media (min-width: 768px) {
  .modal.modal--widget .modal__content {
    max-height: 90vh;
    max-height: 90dvh;
    padding: 2em 2.5em;
    margin: 5vh auto;
  }
}
.modal.modal--widget .close {
  align-self: flex-end;
  padding: 1em;
  margin: 0 0 -1em;
  line-height: 0;
  color: #4a4a4a;
  cursor: pointer;
  background: transparent;
  border: 0;
}
.modal.modal--widget .close__path {
  stroke: #4a4a4a;
}
.modal.modal--widget .contact__title {
  margin-top: 0;
}
`

type ModalOptions = {
  closeModalOnBackdropClick?: boolean
  wrapperElement?: HTMLElement
  rootElement?: Document
}

type InlineWidgetInstance = {
  element: HTMLElement
  publicId: string
  show: () => void
  hide: () => void
}

declare global {
  interface Window {
    KUNDO_INLINE?: {
      instances: InlineWidgetInstance[]
      mount?: (element: HTMLElement) => InlineWidgetInstance | null
    }
  }
}

export default class Modal {
  rootElement: Document
  dialogElement: HTMLDialogElement
  wrapperElement: HTMLElement
  lastFocusElement: HTMLElement
  closeModalOnBackdropClick: boolean

  constructor(dialogElement: HTMLDialogElement, options: ModalOptions = {}) {
    const {
      wrapperElement = document.querySelector<HTMLElement>('.wrapper'),
      closeModalOnBackdropClick = false,
      rootElement = document,
    } = options
    this.rootElement = rootElement
    this.dialogElement = dialogElement
    this.wrapperElement = wrapperElement
    this.closeModalOnBackdropClick = closeModalOnBackdropClick
    this.lastFocusElement = rootElement.activeElement as HTMLElement

    if (!this.dialogElement.hasAttribute('aria-labelledby')) {
      const title = this.dialogElement.querySelector('h1, h2, h3, h4, h5, h6')
      if (title) {
        if (!title.id) {
          title.id = generateUUID()
        }
        this.dialogElement.setAttribute('aria-labelledby', title.id)
      }
    }

    if (this.closeModalOnBackdropClick) {
      this.dialogElement.addEventListener('click', (event) => {
        if (this.dialogElement === event.target) {
          this.close()
        }
      })
    }

    this.dialogElement.addEventListener('close', () => {
      // @ts-expect-error
      this.rootElement.classList.remove('no-scroll')
      if (this.wrapperElement) {
        this.wrapperElement.removeAttribute('aria-hidden')
      }
      if (
        this.lastFocusElement &&
        (this.lastFocusElement.nodeName.toLowerCase() === 'a' ||
          this.lastFocusElement.nodeName.toLowerCase() === 'button')
      ) {
        this.lastFocusElement.focus()
      }
    })

    const closeDialogButtons = this.dialogElement.querySelectorAll('.fn-close')
    closeDialogButtons.forEach((closeDialogButton) => {
      closeDialogButton.addEventListener('click', () => this.close())
    })
  }

  show() {
    this.lastFocusElement = this.rootElement.activeElement as HTMLElement
    this.dialogElement.showModal()

    const possibleFirstFocusElement =
      this.dialogElement.querySelector<HTMLElement>(
        'input:not([type="hidden"]), textarea'
      )
    if (possibleFirstFocusElement) {
      possibleFirstFocusElement.setAttribute('autofocus', 'autofocus')
      possibleFirstFocusElement.focus()
    } else {
      this.dialogElement.setAttribute('tabindex', '-1')
      this.dialogElement.focus()
    }
    // @ts-expect-error
    this.rootElement.classList.add('no-scroll')
    if (this.wrapperElement) {
      this.wrapperElement.setAttribute('aria-hidden', 'true')
    }
  }

  close() {
    this.dialogElement.close()

    const contactForm = this.dialogElement.querySelector('.contact-form')

    if (contactForm) {
      contactForm.removeAttribute('hidden')
    }

    const confirmationStep = this.dialogElement.querySelector('.thank-you')
    if (confirmationStep) {
      confirmationStep.setAttribute('hidden', 'hidden')
    }
  }
}

export function lightboxModal(rootElementRef: Document) {
  // get top most parent element due to polyfill incompabilities
  // when dealing with shadow roots, mostly as a safety check.
  const rootElement = rootElementRef.ownerDocument
    ? rootElementRef.querySelector('body')
    : rootElementRef.body
  const lightboxMedia = rootElement.querySelectorAll<HTMLElement>('article img')
  lightboxMedia.forEach((media) => {
    const initialHTML = media.outerHTML.replace(/width: \d+px;?/g, '')
    media.setAttribute('tabindex', '0')
    media.setAttribute('aria-haspopup', 'true')

    media.addEventListener('keypress', (event) => {
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault()
        media.click()
      }
    })

    media.addEventListener('click', (event) => {
      if (media.closest('a')) {
        return
      }
      event.preventDefault()

      const modalElement = createModalElement(initialHTML)
      rootElement.appendChild(modalElement)

      const modal = new Modal(modalElement, {
        initElement: media,
        closeModalOnBackdropClick: true,
        // @ts-expect-error
        rootElement: rootElement,
      })
      modal.show()
      modalElement.addEventListener('close', () => {
        modalElement.parentElement.removeChild(modalElement)
      })
    })
  })

  function createModalElement(initialHTML: string) {
    const html = `
        <button type="button" class="close fn-close" aria-label="${'Stäng'}">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18">
            <path stroke="currentColor" stroke-width="2" d="M1.5 1.5l15 15m0-15l-15 15" stroke-linecap="round"></path>
          </svg>
        </button>
        <div class="modal__lightbox-content">
          ${initialHTML}
        </div>`

    const modalElement = document.createElement('dialog')
    modalElement.className = 'modal modal--lightbox'
    modalElement.insertAdjacentHTML('afterbegin', html)
    return modalElement
  }
}

export function contactModal(rootElementRef: Document, fetchBaseUrl: string) {
  // get top most parent element due to polyfill incompabilities
  // when dealing with shadow roots, mostly as a safety check.
  const rootElement = rootElementRef.ownerDocument
    ? rootElementRef.querySelector('body')
    : rootElementRef.body
  const contactButtons = rootElement.querySelectorAll<HTMLElement>(
    'a[href="#contactModal"]'
  )
  const modals: Record<string, Modal> = {}
  const pendingFetches: Record<string, Promise<void>> = {}
  contactButtons.forEach((contactButton) => {
    contactButton.setAttribute('role', 'button')
    contactButton.setAttribute('aria-haspopup', 'true')

    contactButton.addEventListener('keypress', (event) => {
      if (event.key === ' ') {
        event.preventDefault()
        contactButton.click()
      }
    })

    contactButton.addEventListener('click', (event) => {
      event.preventDefault()
      const channelId =
        (event.target as HTMLElement).getAttribute('data-channel-id') || ''
      const modalClassName = `modal-${channelId}`

      if (modals[modalClassName]) {
        modals[modalClassName].show()
        return
      }
      if (pendingFetches[modalClassName]) {
        return
      }

      pendingFetches[modalClassName] = fetchContactModalHTML(
        fetchBaseUrl,
        channelId
      )
        .then((text) => {
          rootElement.insertAdjacentHTML('beforeend', text)
          const modalElement = rootElement.querySelector<HTMLDialogElement>(
            `.${modalClassName}`
          )

          const modal = new Modal(modalElement, {
            wrapperElement: rootElement.querySelector('.wrapper'),
            closeModalOnBackdropClick: false,
            // @ts-expect-error
            rootElement,
          })
          modals[modalClassName] = modal
          modal.show()

          const formElement = modalElement.querySelector('form')
          setupContactForm(formElement, fetchBaseUrl)
        })
        .catch((error) => {
          errorMessage(
            `Systemfel, kontaktfunktionen fungerar inte för tillfället. <small><code>${error}</code></small>`
          )
        })
        .finally(() => {
          delete pendingFetches[modalClassName]
        })
    })
  })
}

export function widgetModal(rootElementRef: Document) {
  // When embedded (embed.js), `rootElementRef` is a shadow root, whose `ownerDocument`
  // is truthy. The embed renders the KB — including the widget dialog and its inert
  // <script> — into that shadow root, where the inline-widget script cannot execute or
  // be reached by its document-level auto-scan. That case needs the extra relocation +
  // script-injection handling. The standalone case (document) keeps its original,
  // already-shipped behavior untouched.
  if (rootElementRef.ownerDocument) {
    widgetModalEmbedded(rootElementRef as unknown as ShadowRoot)
  } else {
    widgetModalStandalone(rootElementRef.body)
  }
}

// Standalone help-center pages: the dialog, script and styles are in the real document
// already. Unchanged from the original widget-as-contact-method implementation.
function widgetModalStandalone(rootElement: HTMLElement) {
  const widgetButtons = rootElement.querySelectorAll<HTMLElement>(
    'a[href="#widgetModal"]'
  )
  const modals: Record<string, Modal> = {}

  widgetButtons.forEach((widgetButton) => {
    widgetButton.setAttribute('role', 'button')
    widgetButton.setAttribute('aria-haspopup', 'true')

    widgetButton.addEventListener('keypress', (event) => {
      if (event.key === ' ') {
        event.preventDefault()
        widgetButton.click()
      }
    })

    widgetButton.addEventListener('click', (event) => {
      event.preventDefault()
      const channelId = widgetButton.getAttribute('data-channel-id') || ''
      const modalClassName = `widget-modal-${channelId}`

      if (!modals[modalClassName]) {
        const modalElement = rootElement.querySelector<HTMLDialogElement>(
          `.${modalClassName}`
        )
        if (!modalElement) {
          return
        }
        // Move the dialog out of `.wrapper` so the `aria-hidden`/`display:none`
        // applied to the wrapper while a modal is open does not also hide the
        // dialog (matches the contact modal, which lives at body level).
        rootElement.appendChild(modalElement)
        modals[modalClassName] = new Modal(modalElement, {
          wrapperElement: rootElement.querySelector('.wrapper'),
          closeModalOnBackdropClick: true,
          // @ts-expect-error
          rootElement,
        })
      }

      const modal = modals[modalClassName]
      modal.show()

      window.KUNDO_INLINE?.instances
        .filter((instance) => modal.dialogElement.contains(instance.element))
        .forEach((instance) => instance.show())
    })
  })
}

// Embedded help-center (embed.js / shadow DOM): the widget dialog, its container and
// the inert <script> live in the shadow root. To make the inline widget mount, the
// dialog + container are relocated into the real document.body and the script is loaded
// at document level, where it executes and can mount the container via window.KUNDO_INLINE.
function widgetModalEmbedded(shadowRoot: ShadowRoot) {
  const root = shadowRoot.querySelector('body') as HTMLElement
  if (!root) {
    return
  }
  const widgetButtons = root.querySelectorAll<HTMLElement>(
    'a[href="#widgetModal"]'
  )
  if (!widgetButtons.length) {
    return
  }

  const modals: Record<string, Modal> = {}

  widgetButtons.forEach((widgetButton) => {
    widgetButton.setAttribute('role', 'button')
    widgetButton.setAttribute('aria-haspopup', 'true')

    widgetButton.addEventListener('keypress', (event) => {
      if (event.key === ' ') {
        event.preventDefault()
        widgetButton.click()
      }
    })

    widgetButton.addEventListener('click', (event) => {
      event.preventDefault()
      const channelId = widgetButton.getAttribute('data-channel-id') || ''
      const modalClassName = `widget-modal-${channelId}`

      // Load the inline-widget script + styles lazily on first open, not at page
      // load, so embedded pages with a widget channel don't fetch widget configs
      // for visitors who never open it. Both helpers are idempotent.
      ensureWidgetModalStyles()
      ensureInlineWidgetScript(root)

      if (!modals[modalClassName]) {
        // Take the dialog rendered in the current shadow root and move it to the
        // real document.body so the inline widget can mount into it and host CSS
        // can't affect it. embed.js replaces the shadow body on SPA navigation but
        // not document.body, so drop any dialog relocated on a previous visit first
        // — otherwise stale dialogs (with unmounted containers) accumulate and get
        // reused. The fresh shadow-root dialog is always the source of truth.
        const modalElement = root.querySelector<HTMLDialogElement>(
          `.${modalClassName}`
        )
        if (!modalElement) {
          return
        }
        document.body
          .querySelectorAll<HTMLDialogElement>(`.${modalClassName}`)
          .forEach((stale) => stale.remove())
        document.body.appendChild(modalElement)
        modals[modalClassName] = new Modal(modalElement, {
          wrapperElement: document.querySelector('.wrapper') || undefined,
          closeModalOnBackdropClick: true,
          // Modal uses rootElement for both classList (Element) and activeElement
          // (Document); the body element is what the contact modal passes too.
          // @ts-expect-error
          rootElement: document.body,
        })
      }

      const modal = modals[modalClassName]
      modal.show()

      const container = modal.dialogElement.querySelector<HTMLElement>(
        '[data-kundo-inline]'
      )
      if (container) {
        showInlineWidget(container)
      }
    })
  })
}

// Loads kundo-inline-v1.js into the real document.head once. The script tag rendered
// in the markup does not execute when the help center is embedded (scripts injected
// as shadow-DOM markup don't run), so we re-create it programmatically in the top
// document, where it does execute and where it can auto-scan/mount [data-kundo-inline].
function ensureInlineWidgetScript(root: HTMLElement) {
  if (document.querySelector('script[data-kundo-inline-widget]')) {
    return
  }
  const rendered = root.querySelector<HTMLScriptElement>(
    'script[src*="kundo-inline-v1"]'
  )
  if (!rendered) {
    return
  }
  const script = document.createElement('script')
  script.src = rendered.src
  script.async = true
  script.dataset.kundoInlineWidget = 'true'
  document.head.appendChild(script)
}

// Mounts/reveals the inline widget for a specific container. The inline script loads
// asynchronously, so on the first (cold) open window.KUNDO_INLINE may not exist yet —
// wait for it before mounting. Prefers the explicit mount() API (registers just this
// element, no global re-scan); falls back to the auto-scanned instance for the
// standalone case where the container was in the document at script load.
async function showInlineWidget(container: HTMLElement) {
  const api = await waitForInlineWidgetApi()
  if (!api) {
    errorMessage(
      'Systemfel, formuläret kan inte laddas för tillfället. Försök igen senare.'
    )
    return
  }
  const instance =
    api.mount?.(container) ??
    api.instances.find((i) => i.element === container)
  instance?.show()
}

function waitForInlineWidgetApi(): Promise<Window['KUNDO_INLINE'] | undefined> {
  const timeoutMs = 10000
  const intervalMs = 50
  return new Promise((resolve) => {
    if (window.KUNDO_INLINE) {
      resolve(window.KUNDO_INLINE)
      return
    }
    const startedAt = Date.now()
    const timer = setInterval(() => {
      if (window.KUNDO_INLINE) {
        clearInterval(timer)
        resolve(window.KUNDO_INLINE)
      } else if (Date.now() - startedAt > timeoutMs) {
        clearInterval(timer)
        resolve(undefined)
      }
    }, intervalMs)
  })
}

// The widget dialog is relocated to the real document.body, outside the shadow root
// where the help-center stylesheet lives. Inject the few modal rules it needs at
// document level so the relocated dialog is styled without exporting the whole KB
// stylesheet into the host page.
function ensureWidgetModalStyles() {
  if (document.getElementById('kundo-widget-modal-styles')) {
    return
  }
  const style = document.createElement('style')
  style.id = 'kundo-widget-modal-styles'
  style.textContent = WIDGET_MODAL_CSS
  document.head.appendChild(style)
}

async function fetchContactModalHTML(fetchBaseUrl: string, channelId: string) {
  const response = await fetch(`${fetchBaseUrl}/contact?id=${channelId}`, {
    credentials: 'same-origin',
  })
  if (response.ok) {
    return response.text()
  }
  throw new Error(response.statusText)
}

function setupContactForm(formElement: HTMLFormElement, fetchBaseUrl: string) {
  const validation = new FormValidation(formElement)
  validation.addInputValidateListenersInput()
  const spinner = formElement.querySelector<HTMLElement>(
    '.contact-form-spinner'
  )

  const fileInput = formElement.querySelector<HTMLInputElement>(
    ".form-group > input[type='file']"
  )
  let persistentFileList = []

  formElement.addEventListener('submit', (submitEvent) => {
    submitEvent.preventDefault()
    spinner.style.display = 'block'
    const submitter = submitEvent.submitter as HTMLButtonElement
    if (submitter) {
      submitter.disabled = true
    }
    const response = ajaxSendForm(formElement, fetchBaseUrl)
      .then(async (response) => {
        let json
        if (response.status === 413) {
          // Special case for too large file uploads
          json = getFileSizeTooLargeJson()
        } else if (response.status === 408) {
          // Client request timeout, needs to inform user to try again
          setTechnicalErrorRetryMessage()
        } else {
          // Normal case (i.e. JSON is expected)
          validateContentTypeJSON(response)
          json = await response.json()
          if (response.status === 202) {
            if (fileInput !== null) {
              // Clear file list if present (as kept 'persistently' needs to be done manually)
              clearFileList(
                fileInput,
                formElement.querySelector<HTMLDivElement>(
                  '.file-list-div'
                )
              )
            }

            dispatchContactFormSubmittedEvent(formElement)
            formElement.setAttribute('hidden', 'hidden')
            formElement.reset()
            showThankYouElement()
          }
        }
        validation.removeErrorsFromForm()
        validation.jsonapiErrors(json)
        validation.setFocusOnFirstError()
      })
      .catch((error) => {
        errorMessage(
          `Systemfel, kontaktfunktionen fungerar inte för tillfället. <small><code>${error}</code></small>`,
          { element: formElement, position: 'afterend' }
        )
      })
      .finally(() => {
        spinner.style.display = 'none'
        if (submitter) {
          submitter.disabled = false
        }
      })
  })

  if (fileInput !== null) {
    let formGroup: HTMLElement = fileInput.parentElement
    let fileListDiv: HTMLDivElement = document.createElement('div')
    fileListDiv.className = 'file-list-div col-md-6'
    fileInput.insertAdjacentElement('afterend', fileListDiv)

    const uploadButtonText = fileInput.getAttribute('data-button-text')
    const uploadButtonHtml = `<button class="btn btn--secondary btn--with-icon btn--no-side-margins contact-form-upload-button">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clip-path="url(#clip0_436_352)">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M19.0715 4.92893C17.9306 3.788 15.9715 3.7863 14.6942 5.06362L5.06407
            14.6937C3.78675 15.9711 3.78845 17.9301 4.92938 19.0711C5.69725 19.8389 6.8214 20.0903 7.87666 19.7858C8.4073 19.6327
            8.96158 19.9388 9.11467 20.4694C9.26777 21.0001 8.96171 21.5544 8.43107 21.7075C6.73688 22.1963 4.84113 21.8112 3.51517
            20.4853C1.5319 18.502 1.65421 15.2752 3.64985 13.2795L13.28 3.64941C15.2756 1.65376 18.5025 1.53146 20.4857 3.51472C22.469
            5.49798 22.3467 8.72483 20.351 10.7205L12.9433 18.1283C11.7658 19.3057 9.83548 19.3978 8.63327 18.1956C7.43106 16.9934 7.52321
            15.063 8.70062 13.8856L16.2933 6.2929C16.6839 5.90237 17.317 5.90237 17.7076 6.2929C18.0981 6.68342 18.0981 7.31659 17.7076
            7.70711L10.1148 15.2998C9.65575 15.7589 9.6876 16.4215 10.0475 16.7814C10.4074 17.1413 11.07 17.1731 11.529 16.714L18.9368
            9.30626C20.2141 8.02894 20.2124 6.06987 19.0715 4.92893Z" fill="#676C79"/>
          </g>
          <defs>
            <clipPath id="clip0_436_352">
              <rect width="24" height="24" fill="white"/>
            </clipPath>
          </defs>
        </svg>
          ${uploadButtonText}
      </button>`

    const htmlParser = new DOMParser()
    const uploadButton = htmlParser.parseFromString(
      uploadButtonHtml,
      'text/html'
    ).body.firstChild

    formGroup.insertBefore(uploadButton, formGroup.firstChild)
    formGroup.querySelector('label').className = 'file-upload-label'

    uploadButton.addEventListener('click', (event) => {
      event.preventDefault()
      fileInput.click()
    })

    fileInput.addEventListener('change', (event) => {
      persistentFileList = persistentFileList.concat(
        Array.from(fileInput.files)
      )
      updateFileList(fileInput, persistentFileList)
      renderFileList(fileInput, persistentFileList, fileListDiv)
    })
  }

  function setTechnicalErrorRetryMessage() {
    let retryMessage = document.getElementById(
      'technical-error-retry-message'
    ).innerHTML
    errorMessage(retryMessage, { element: formElement, position: 'afterend' })
  }

  function getFileSizeTooLargeJson() {
    if (fileInput !== null) {
      // input id will always have the form "contact_{unique-id} when generated in backend"
      const fileInputPointer = '/' + fileInput.getAttribute('id').split('_')[1]
      const errorMessage = fileInput.getAttribute('data-file-size-too-large')
      return {
        errors: [
          {
            detail: errorMessage,
            source: { pointer: fileInputPointer },
          },
        ],
      }
    }
    return {}
  }

  function removeFileFromList(
    fileInput: HTMLInputElement,
    persistentFileList: File[],
    fileListDiv: HTMLDivElement,
    index_to_remove: number
  ) {
    persistentFileList.splice(index_to_remove, 1)
    updateFileList(fileInput, persistentFileList)
    renderFileList(fileInput, persistentFileList, fileListDiv)
  }

  function updateFileList(
    fileInput: HTMLInputElement,
    persistentFileList: File[]
  ) {
    let dt = new DataTransfer()
    persistentFileList.forEach((item) => {
      dt.items.add(item)
    })
    fileInput.files = dt.files
  }

  function renderFileList(
    fileInput: HTMLInputElement,
    persistentFileList: File[],
    fileListDiv: HTMLDivElement
  ) {
    validation.removeErrorFeedback(fileInput.getAttribute('name'))
    fileListDiv.innerHTML = ''
    Array.from(fileInput.files).forEach((file, index) => {
      fileListDiv.appendChild(
        createFileListDivItem(
          fileInput,
          persistentFileList,
          fileListDiv,
          file,
          index
        )
      )
    })
  }

  function createFileListDivItem(
    fileInput: HTMLInputElement,
    persistentFileList: File[],
    fileListDiv: HTMLDivElement,
    file: File,
    index: number
  ) {
    const container: HTMLDivElement = document.createElement('div')
    container.className = 'panel panel--small'
    const fileContainer: HTMLDivElement = document.createElement('div')
    fileContainer.className = 'file-container'
    const fileName: HTMLSpanElement = document.createElement('span')
    const fileSize: HTMLSpanElement = document.createElement('span')
    fileSize.className = 'file-size'
    const removeButton: HTMLButtonElement = document.createElement('button')
    removeButton.className = 'btn btn--with-icon btn--small'
    const removeButtonSvg = `<svg
        xmlns="http://www.w3.org/2000/svg"
        width="1em"
        height="1em"
        fill="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          fill-rule="evenodd"
          d="M7.293 8.707a1 1 0 0 1 1.414-1.414L12 10.586l3.293-3.293a1 1 0 1 1 1.414 1.414L13.414
          12l3.293 3.293a1 1 0 0 1-1.414 1.414L12 13.414l-3.293 3.293a1 1 0 0 1-1.414-1.414L10.586
          12 7.293 8.707Z"
          clip-rule="evenodd"
        ></path>
      </svg>`
    removeButton.innerHTML = removeButtonSvg
    removeButton.type = 'button'

    fileName.innerHTML = file.name
    fileSize.innerHTML = ` (${formatBytes(file.size)})`
    fileContainer.innerHTML = fileName.outerHTML + fileSize.outerHTML

    container.appendChild(fileContainer)
    container.appendChild(removeButton)
    removeButton.onclick = function () {
      removeFileFromList(fileInput, persistentFileList, fileListDiv, index)
      return false
    }
    return container
  }

  function clearFileList(
    fileInput: HTMLInputElement,
    fileListDiv: HTMLDivElement
  ) {
    persistentFileList = []
    updateFileList(fileInput, persistentFileList)
    fileListDiv.innerHTML = ''
  }

  function showThankYouElement() {
    const confirmationStep =
      formElement.parentNode.querySelector<HTMLElement>('.thank-you')
    confirmationStep.removeAttribute('hidden')
    confirmationStep.setAttribute('tabindex', '-1')
    confirmationStep.focus()
  }
}

function formatBytes(bytes) {
  if (bytes < 1000) {
    // Size is below 1 KB, return value in bytes
    return bytes + ' bytes'
  } else if (bytes < 1000 * 1000) {
    // Size is between 1 KB and 1 MB, return value in kilobytes without decimal points if above 1 KB
    const kilobytes = bytes / 1000
    return Math.floor(kilobytes) + ' KB'
  } else {
    // Size is equal to or over 1 MB, return value in megabytes with maximum one decimal
    const megabytes = (bytes / (1000 * 1000)).toFixed(1)
    return megabytes.endsWith('.0')
      ? megabytes.slice(0, -2) + ' MB'
      : megabytes + ' MB'
  }
}

function ajaxSendForm(formElement: HTMLFormElement, fetchBaseUrl: string) {
  const formData = new FormData(formElement)
  const actionPath = formElement.getAttribute('action')
  return fetch(`${fetchBaseUrl}${actionPath}`, {
    credentials: 'include',
    method: formElement.getAttribute('method'),
    body: formData,
    headers: {
      Accept: 'application/vnd.api+json, application/json, */*',
    },
  })
}

function validateContentTypeJSON(response: Response) {
  const contentType = response.headers.get('content-type')
  const isJSON =
    contentType &&
    (contentType.indexOf('application/vnd.api+json') !== -1 ||
      contentType.indexOf('application/json') !== -1)
  if (!isJSON) {
    throw new Error("Oops, we haven't got JSON")
  }
  return response
}

function dispatchContactFormSubmittedEvent(form: HTMLFormElement) {
  const formData = new FormData(form)
  // @ts-expect-error
  const data = Object.fromEntries(formData.entries())
  delete data._csrf_token
  document.dispatchEvent(
    new CustomEvent('kundo-knowledge:contact-form-submitted', {
      detail: data,
      bubbles: true,
      cancelable: true,
      composed: true,
    })
  )
}
